import { Button, Modal } from '@plick/ui';
import ReactPlayer from 'react-player';
import { IconExternalLink } from '@tabler/icons-react';
import { Announcement } from '@interfaces/general';
import { plickTheme } from '@theme/index';
import { useSurveyShownOnce } from '@utils/analytics/useSurveyShownOnce';
import { AnnouncementOptionButtons } from '../shared/AnnouncementOptionButtons';
import { useRespondAnnouncement } from './hooks/useRespondAnnouncement';

// Alto máximo del modal de anuncio. El <Modal> de @plick/ui lo centra verticalmente; con este tope
// reservamos ~72px arriba y ~72px abajo: arriba para que el borde superior y la X (que flota fuera de
// la esquina) queden bajo el header fijo (52px móvil / 64px desktop) con holgura; abajo para que el
// modal llegue casi hasta la barra inferior (`NavBarMobile` 60px), dejando solo un margen chico. Con
// el anuncio del taller (video + textos) el contenido excede este alto → el modal se llena y el footer
// sticky (CTA) queda anclado abajo, cerca de la barra; los anuncios cortos siguen como card compacta.
// `dvh` (no `vh`) respeta la barra de direcciones dinámica del móvil. Vale incluso en 375×667.
const ANNOUNCEMENT_MODAL_MAX_HEIGHT = 'calc(100dvh - 144px)';

interface Props {
  announcement: Announcement;
  isOpen: boolean;
  onClose: () => void;
}

export const CommunicationsModal = ({ announcement, isOpen, onClose }: Props) => {
  // Cada componente tiene su propia instancia para que isSubmitting sea local
  // (un click en el modal no debe deshabilitar otros banners/cards activos).
  const { respond, dismiss, isSubmitting, submittingOptionId } = useRespondAnnouncement('modal');

  // MEAS-1: anuncio CON opciones visible en el modal → survey_shown (1 vez por comercio+encuesta).
  const hasOptions = Boolean(announcement.options?.length);
  useSurveyShownOnce(announcement.uuid, 'modal', isOpen && hasOptions);

  const bgColor = announcement.bg_color || plickTheme.colors.light;
  const textColor = announcement.text_color || plickTheme.colors.dark;

  // Fallback CTA: si el backend manda `url` top-level y `options` vacio,
  // renderizamos un boton que abre la URL en pestana nueva (mismo patron
  // que CommunicationsCard).
  const hasMainUrl = Boolean(announcement.url) && !announcement.options?.length;

  return (
    <Modal
      padding="0rem"
      maxHeight={ANNOUNCEMENT_MODAL_MAX_HEIGHT}
      isOpen={isOpen}
      onClose={() => {
        // is_persistent bloquea cierre manual; isSubmitting evita doble ejecución
        if (!announcement.is_persistent && !isSubmitting) {
          if (announcement.show_once && !announcement.user_response) {
            dismiss(announcement.uuid, { dismissed: true });
          }
          onClose();
        }
      }}
      closableWaitingTime={(announcement.delay_before_close || 0) * 1000}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      {/* Patrón canónico del repo (ver ConfirmationModal/WhatsappQrModal):
          hijos directos del <Modal>, sticky se ancla al ModalContent que ya
          maneja maxHeight + scroll. */}

      {/* Video — block normal, NO sticky.
          sticky crea un nuevo stacking context que choca con el iframe de
          YouTube (que renderiza en su propio plano de composicion) y deja
          al CloseButton del Modal atrapado detras. Mantenemos el video
          como elemento normal del scroll. */}
      {announcement.video_url && (
        // Video responsive 16:9 (CSS `aspect-ratio`) en vez de height:40vh fijo: 40vh (~267px en 667)
        // se comía medio panel y cortaba el título; el 16:9 escala con el ancho (~193px en un modal de
        // 343px) dejando ver título + subtítulo completos. IMPORTANTE: el contenedor NO lleva `position`
        // — si fuera posicionado, al ir DESPUÉS de la X (CloseButton `absolute`, primer hijo del Content)
        // en el DOM la taparía (mismo motivo del comentario de arriba); estático pinta antes → X encima.
        <div style={{ backgroundColor: bgColor, width: '100%', aspectRatio: '16 / 9' }}>
          <ReactPlayer url={announcement.video_url} width="100%" height="100%" controls />
        </div>
      )}

      {/* Cuerpo */}
      <div className="px-6 pt-8 pb-6" style={{ backgroundColor: bgColor, color: textColor }}>
        <h1 id="modal-title" className="text-3xl font-bold text-center mb-4 break-words">
          {announcement.title}
        </h1>

        {announcement.subtitle && (
          <h2 id="modal-description" className="text-xl text-center mb-4 opacity-90 break-words">
            {announcement.subtitle}
          </h2>
        )}

        {(announcement.long_description || announcement.text) && (
          <p className="mt-4 text-center text-lg opacity-80 whitespace-pre-wrap break-words">
            {announcement.long_description || announcement.text}
          </p>
        )}
      </div>

      {/* Footer sticky con CTAs */}
      {announcement.options && announcement.options.length > 0 && (
        <div
          className="sticky bottom-0 left-0 right-0 px-6 py-4 border-t"
          style={{ backgroundColor: bgColor, borderColor: `${textColor}1A`, color: textColor }}
        >
          <AnnouncementOptionButtons
            options={announcement.options}
            announcementId={announcement.uuid}
            userResponse={announcement.user_response}
            bgColor={bgColor}
            textColor={textColor}
            isSubmitting={isSubmitting}
            submittingOptionId={submittingOptionId}
            isPersistent={announcement.is_persistent === true}
            variant="modal"
            respond={respond}
            onComplete={onClose}
            dismiss={announcement.show_once ? dismiss : undefined}
          />
        </div>
      )}

      {hasMainUrl && (
        <div
          className="sticky bottom-0 left-0 right-0 px-6 py-4 border-t [&>button]:w-full"
          style={{ backgroundColor: bgColor, borderColor: `${textColor}1A` }}
        >
          <Button.Primary
            text="Más información"
            leftIcon={<IconExternalLink size={16} />}
            onClick={() => {
              // Tratamos el click en CTA como interaccion suficiente y cerramos.
              // Sin esto, un announcement con is_persistent + url + options=[]
              // dejaria al usuario atrapado: la X esta deshabilitada por
              // is_persistent y no hay otra opcion para dismissar.
              window.open(announcement.url ?? '#', '_blank', 'noopener,noreferrer');
              if (announcement.show_once && !announcement.user_response) {
                dismiss(announcement.uuid, { cta_clicked: true });
              }
              onClose();
            }}
          />
        </div>
      )}
    </Modal>
  );
};
